import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { AuthContext } from '../../contexts/AuthContext';
import Card from '../UI/Card';
import Button from '../UI/Button';
import Badge from '../UI/Badge';

export default function BoardDetail() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPost();
  }, [postId]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/board/${postId}`);
      setPost(response.data.post);
      setComments(response.data.post.comments || []);
    } catch (err) {
      setError(err.response?.data?.error || '게시글을 불러올 수 없습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();

    if (!newComment.trim()) return;

    try {
      setSubmitting(true);
      const response = await api.post(`/board/${postId}/comments`, {
        content: newComment
      });

      if (response.data.success) {
        setComments([...comments, response.data.comment]);
        setNewComment('');
      }
    } catch (err) {
      alert(err.response?.data?.error || '댓글 작성에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('댓글을 삭제하시겠습니까?')) return;

    try {
      await api.delete(`/board/comments/${commentId}`);
      setComments(comments.filter(c => c.id !== commentId));
    } catch (err) {
      alert(err.response?.data?.error || '댓글 삭제에 실패했습니다.');
    }
  };

  const handleDeletePost = async () => {
    if (!window.confirm('게시글을 삭제하시겠습니까?')) return;

    try {
      await api.delete(`/board/${postId}`);
      navigate('/board');
    } catch (err) {
      alert(err.response?.data?.error || '게시글 삭제에 실패했습니다.');
    }
  };

  const getCategoryInfo = (category) => {
    const info = {
      COLLABORATION: { label: '협업 제안', variant: 'primary' },
      JOINT_CLASS: { label: '공동 수업', variant: 'secondary' },
      RESOURCE_SHARE: { label: '자료 공유', variant: 'accent' },
      QUESTION: { label: '질문', variant: 'warning' },
      ANNOUNCEMENT: { label: '공지사항', variant: 'danger' },
    };
    return info[category] || { label: category, variant: 'default' };
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <Card className="text-center py-8">
        <p className="text-danger-500 mb-4">{error || '게시글을 찾을 수 없습니다.'}</p>
        <Button onClick={() => navigate('/board')}>목록으로</Button>
      </Card>
    );
  }

  const catInfo = getCategoryInfo(post.category);
  const isAuthor = user && user.id === post.authorId;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Post */}
      <Card>
        <div className="flex items-start gap-3 mb-4">
          <Badge variant={catInfo.variant}>{catInfo.label}</Badge>
          {isAuthor && (
            <button
              onClick={handleDeletePost}
              className="ml-auto text-danger-500 hover:text-danger-600 text-sm"
            >
              삭제
            </button>
          )}
        </div>

        <h1 className="text-3xl font-bold text-neutral-800 mb-4">{post.title}</h1>

        <div className="flex items-center gap-4 text-sm text-neutral-500 pb-4 border-b border-neutral-100">
          <span className="font-medium">{post.author?.username || '알 수 없음'}</span>
          {post.author?.schoolName && <span>{post.author.schoolName}</span>}
          <span>{new Date(post.createdAt).toLocaleString('ko-KR')}</span>
          <span>조회 {post.viewCount || 0}</span>
        </div>

        <div className="prose max-w-none py-6">
          <p className="text-neutral-700 whitespace-pre-wrap leading-relaxed">
            {post.content}
          </p>
        </div>
      </Card>

      {/* Comments */}
      <Card>
        <h2 className="text-xl font-bold text-neutral-800 mb-4">
          댓글 ({comments.length})
        </h2>

        <div className="space-y-4 mb-6">
          {comments.map((comment) => (
            <div key={comment.id} className="p-4 bg-neutral-50 rounded-xl">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-neutral-800">
                    {comment.author?.username || '알 수 없음'}
                  </span>
                  {comment.author?.schoolName && (
                    <span className="text-sm text-neutral-500">
                      {comment.author.schoolName}
                    </span>
                  )}
                </div>
                {user && user.id === comment.authorId && (
                  <button
                    onClick={() => handleDeleteComment(comment.id)}
                    className="text-danger-500 hover:text-danger-600 text-sm"
                  >
                    삭제
                  </button>
                )}
              </div>

              <p className="text-neutral-700 mb-2 whitespace-pre-wrap">
                {comment.content}
              </p>

              <span className="text-sm text-neutral-500">
                {new Date(comment.createdAt).toLocaleString('ko-KR')}
              </span>
            </div>
          ))}

          {comments.length === 0 && (
            <p className="text-center text-neutral-500 py-8">
              첫 댓글을 작성해보세요.
            </p>
          )}
        </div>

        {/* Comment Form */}
        <form onSubmit={handleCommentSubmit} className="space-y-3">
          <textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            className="w-full px-4 py-2.5 rounded-xl border-2 border-neutral-200 focus:border-primary-500 focus:outline-none transition-colors duration-200 min-h-[100px]"
            placeholder="댓글을 입력하세요..."
          />
          <div className="flex justify-end">
            <Button type="submit" disabled={submitting || !newComment.trim()}>
              {submitting ? '작성 중...' : '댓글 작성'}
            </Button>
          </div>
        </form>
      </Card>

      <div className="text-center">
        <Button variant="outline" onClick={() => navigate('/board')}>
          목록으로 돌아가기
        </Button>
      </div>
    </div>
  );
}
